import { isBillingProfileComplete, type Organization } from '@noconflict/contracts';
import { useTranslation } from 'react-i18next';
import { useBillingProfile } from '../../entities/billing-profile';
import { useAccountPublic } from '../../entities/ledger';
import { AccountBalanceCard } from '../../features/billing/account-balance';
import { PaymentMethodList } from '../../features/billing/payment-method-list';
import { BillingProfileForm } from '../../features/org/billing-profile-form';
import { useFormat } from '../../shared/i18n/format';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../shared/ui/card';
import { ErrorState } from '../../shared/ui/error-state';
import { Notice } from '../../shared/ui/notice';
import { Skeleton } from '../../shared/ui/skeleton';

/**
 * Organization settings → Billing (`billing.view`; the parent does not mount it otherwise, so nothing is asked):
 * the billing profile, the account balance and history while amounts are visible, and stored cards in card mode.
 * With `billing.manage` it is the form and the cards can be changed; without, the same details read-only. Both
 * roles that hold `billing.view` hold `billing.manage` today, but the two keys stay separate so a view-only
 * billing role can exist.
 */
export function BillingPanel({ org, canManage }: { org: Organization; canManage: boolean }) {
  // An org on account pays no card, so it has no cards to manage; with amounts hidden it has no balance to see.
  const account = useAccountPublic(org.id);
  return (
    <div className="grid gap-6">
      <BillingProfileCard org={org} canManage={canManage} />
      {account.data?.amounts_visible ? <AccountBalanceCard organizationId={org.id} /> : null}
      {account.data?.payment_mode === 'card' ? (
        <PaymentMethodList organizationId={org.id} canManage={canManage} />
      ) : null}
    </div>
  );
}

/** The legal entity we bill, where it is, and who receives receipts. */
function BillingProfileCard({ org, canManage }: { org: Organization; canManage: boolean }) {
  const { t } = useTranslation('org');
  const { formatDate } = useFormat();
  const profile = useBillingProfile(org.id);
  return (
    <Card>
      <CardHeader>
        <CardTitle asChild className="font-sans">
          <h2>{t('settings.billing.title')}</h2>
        </CardTitle>
        <CardDescription>{t('settings.billing.help')}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6">
        {profile.isPending ? (
          <section
            aria-busy="true"
            aria-label={t('settings.billing.loading')}
            className="grid gap-3"
          >
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-2/3" />
          </section>
        ) : null}
        {profile.isError ? (
          <ErrorState
            size="compact"
            title={t('settings.billing.error')}
            description={profile.error.message}
            onRetry={() => void profile.refetch()}
          />
        ) : null}
        {profile.isSuccess && !isBillingProfileComplete(profile.data) ? (
          <Notice tone="warning">{t('settings.billing.incomplete')}</Notice>
        ) : null}
        {profile.isSuccess && canManage ? (
          <BillingProfileForm
            key={profile.data?.updated_at ?? 'new'}
            org={org}
            profile={profile.data}
          />
        ) : null}
        {profile.isSuccess && !canManage ? (
          <>
            <Notice>{t('settings.billing.readOnly')}</Notice>
            {profile.data ? (
              <dl className="grid gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-bold text-heading">
                    {t('settings.billing.legalName')}
                  </dt>
                  <dd>{profile.data.legal_name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-bold text-heading">
                    {t('settings.billing.emailsTitle')}
                  </dt>
                  <dd className="wrap-anywhere">{profile.data.billing_emails.join(', ')}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-sm font-bold text-heading">{t('settings.billing.line1')}</dt>
                  <dd>
                    {[
                      profile.data.line1,
                      profile.data.line2,
                      profile.data.city,
                      profile.data.region,
                      profile.data.postal_code,
                      profile.data.country,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-bold text-heading">
                    {t('settings.general.updated')}
                  </dt>
                  <dd>{formatDate(profile.data.updated_at)}</dd>
                </div>
              </dl>
            ) : null}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
